import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AppSettingsService } from './app-settings.service';
import { AuthorizationService } from './authorization.service';
import { GroupContextService } from './group-context.service';
import { FeatureToggleService } from './feature-toggle.service';
import { hd } from './helpdesk.typings';
import { shareReplay } from 'rxjs/operators';
import { DeviceInfoModel } from '../../apps/xsight/src/app/modules/settings/components/privacy-settings/privacy-settings.models';

interface CheckinActionTemporaryInterface {
  deviceIds: string;
  timeoutInMilliseconds: number;
  overrideCheckinThrottle: boolean;
  watchForCheckin: boolean;
}
export interface IDeviceActionInfo {
  Action: string;
  Message: string;
  QueueForOfflineDelivery: boolean;
}

export interface IDeviceModel {
  /** The device identifier */
  deviceId: string;
  /** The name of the device */
  deviceName: string;
  /** The Url to mobicontrol device page */
  deviceDetailsUrl: string;
  /** The MC deployment server name */
  deploymentServerName: string;
  /** The kind of device */
  kind: string;
  /** The family of the device */
  family: string;
  /** The os version */
  osVersion: string;
  /** The name or IP address of the device */
  hostName: string;
  /** True if the device agent is online; otherwise, false */
  isAgentOnline: boolean;
  /** True if the device agent supports remote view only; otherwise, false */
  isRemoteViewOnly: boolean;
  /** True if the device agent supports remote control; otherwise, false */
  supportsRC: boolean;
  /** MAC address */
  macAddress: string;
  /** Device manufacturer */
  manufacturer: string;
  /** Device model */
  model: string;
  /** Phone number */
  phoneNumber: string;
  /** Hardware serial number */
  hardwareSerialNumber: string;
  /** User name */
  userName: string;
  /** User phone number */
  userPhoneNumber: string;
  /** IMEI */
  imei: string;
  /** The external MC connection identifier */
  mcConnectionId?: number;
  /** The external MC device identifier */
  mcDeviceId: string;
  /** The external MC connection name */
  $$mcConnectionName: string;
  /** Flag whether mc connection is default */
  $$isDefaultMcConnection?: boolean;
}

/**
 * Defines MobiControl device permissions for a specific device.
 */
export interface IRemoteControlPermissionsModel {
  /** The device ID for the permissons */
  deviceId: string;
  /** Contains a bitwise combination of device permissions (RemoteControlPermissions constants) */
  permissions: number;
}

/** MobiControl remote control device permission constants */
export const enum RemoteControlPermissions {
  /** The right to remote control devices. */
  RemoteControl = 0x000001,

  /** The right to view the device file system during remote control. */
  RemoteControlViewFiles = 0x000002,

  /** The right to update the device file system during remote control. */
  RemoteControlUpdateFiles = 0x000004,

  /** The right to view the device registry during remote control. */
  RemoteControlViewRegistry = 0x000008,

  /** The right to update the device registry during remote control. */
  RemoteControlUpdateRegistry = 0x000010,

  /** The right to view the task manager tool during remote control. */
  RemoteControlViewTasks = 0x000020,

  /** The right to terminate tasks using the task manage tool during remote control. */
  RemoteControlManageTasks = 0x000040,

  /** The right to view device system info during remote control. */
  RemoteControlViewSystemInfo = 0x000080,

  /** The right to send DOS/Shell commands during remote control. */
  RemoteControlCommands = 0x000100,

  /** The right to run device-side scripts during remote control. */
  RemoteControlScripting = 0x000200,

  /** The right to send keyboard and mouse input to the device during remote control. */
  RemoteControlInput = 0x000400,

  /** The right to remote control devices without notifying the device user. */
  RemoteControlWithoutNotification = 0x000800,

  /** The right to connect through ssl tunnel service to the agent host during remote control. */
  RemoteControlWebConsole = 0x001000,
}

export interface IConnectionInfo {
  RecentMcConnectionId: string;
}

@Injectable({
  providedIn: 'root',
})
export class XDeviceService {
  private readonly GROUP_HEADER_ID = 'hd-group-id';
  private devicePermSubscriptionMap = {};

  constructor(
    private http: HttpClient,
    private appSettingsService: AppSettingsService,
    private authorizationService: AuthorizationService,
    private groupContextService: GroupContextService,
    private featureToggleService: FeatureToggleService
  ) {}

  /**
   * Performs a device search for one or more MobiControl instances.
   *
   * If one or more tokens are include in the mcTokens array, all MobiControl instances for the
   * included tokens will be searched, as well as the default/master MobiControl instance.
   * If no tokens are provided, then only the default/master MobiControl instance will be searched.
   *
   * @param searchText The search text
   * @param excludeIds Optional list of device ids to exclude from the search
   * @param mcTokens Optional list of tokens for external MobiControl instances to include in the search
   * @param excludeDefault Optional flag indicating whether the default MobiControl instance should be excluded from the list
   * @return Observable for the search request
   */
  public searchDevices = (
    searchText: string,
    excludeIds?: string[],
    connectionIds?: string[]
  ): Observable<IDeviceModel[]> => {
    let headers = this.addGroupHeaders(new HttpHeaders());
    const options = {
      headers: headers,
      params: { searchText: searchText },
    };

    if (excludeIds && excludeIds.length > 0) {
      options.params['excludeIds'] = excludeIds.join(',');
    }

    if (connectionIds && connectionIds.length > 0) {
      options.params['connectionIds'] = connectionIds.join(',');
    }

    return this.http.get<IDeviceModel[]>(this.appSettingsService.deviceServiceUrl, options);
  };

  /**
   * Gets device information for the specified device id.
   * @param deviceId The device id
   * @return Observable for the device details request
   */
  public getDevice = (deviceId: string): Observable<IDeviceModel> => {
    // Always attach all MC headers in case the specified device belongs to a different MC
    let headers = this.addGroupHeaders(new HttpHeaders());
    const options = {
      headers: headers,
    };

    return this.http.get<IDeviceModel>(this.appSettingsService.deviceServiceUrl + deviceId, options);
  };

  /**
   * Gets last searched device Id
   * @return Observable for the device Id request
   */
  public getLastMcId = (): Observable<IConnectionInfo> => {
    return this.http.get<IConnectionInfo>(this.appSettingsService.deviceServiceUrl + 'config/search');
  };

  public requestDevicesCheckin = (
    associatedDevices: string,
    overrideThrottling?: boolean,
    watchForCheckin?: boolean,
    timeoutInMilliseconds?: number
  ): Observable<any> => {
    let headers = this.addGroupHeaders(new HttpHeaders());
    const options = {
      headers: headers,
    };
    if (overrideThrottling == null) {
      overrideThrottling = false;
    }
    if (watchForCheckin == null) {
      watchForCheckin = false;
    }
    const body: CheckinActionTemporaryInterface = {
      deviceIds: associatedDevices,
      overrideCheckinThrottle: overrideThrottling,
      watchForCheckin: watchForCheckin,
      timeoutInMilliseconds: timeoutInMilliseconds,
    };

    return this.http.post<any>(this.appSettingsService.deviceServiceUrl + 'checkinDevices', body, options);
  };

  public sendDeviceAction = (deviceId: string, actionInfo: IDeviceActionInfo) => {
    let headers = this.addGroupHeaders(new HttpHeaders());
    const options = {
      headers: headers,
    };

    const body = {
      DeviceId: deviceId,
      ActionInfo: JSON.stringify(actionInfo),
    };

    return this.http.post<any>(this.appSettingsService.deviceServiceUrl + 'deviceAction', body, options);
  };

  public createUnmanagedDevice = (device: hd.IUnmanagedDeviceModel): Observable<hd.IUnmanagedDeviceModel> => {
    let headers = this.addGroupHeaders(new HttpHeaders());
    const options = {
      headers: headers,
    };
    return this.http.post<hd.IUnmanagedDeviceModel>(
      this.appSettingsService.deviceServiceUrl + 'unmanaged?unmanagedDeviceName=' + device.deviceName,
      device,
      options
    );
  };

  /**
   * Returns the full CSS selector classes for the given device family.
   * @param deviceFamily Device family to lookup CSS class for.
   */
  public getPlatformIconClass = (deviceFamily: string): string => {
    const platformIconClass = this.generatePlatformIconString(this.getPlatformCssClass(deviceFamily));
    return platformIconClass;
  };

  public getDevicePermissions = (deviceId: string): Observable<IRemoteControlPermissionsModel> => {
    if (!this.devicePermSubscriptionMap[deviceId]) {
      let headers = this.addGroupHeaders(new HttpHeaders());
      const options = {
        headers: headers,
      };
      this.devicePermSubscriptionMap[deviceId] = this.http
        .get<any>(this.appSettingsService.deviceServiceUrl + deviceId + '/permissions', options)
        .pipe(shareReplay(1));
    }
    return this.devicePermSubscriptionMap[deviceId];
  };

  /**
   * Returns the _single_ css class representing the icon for the given device family
   * @param deviceFamily The device family (or platform)
   */
  public getPlatformCssClass = (deviceFamily: string): string => {
    let platformCssClass;

    switch (deviceFamily) {
      case 'WindowsCE':
        platformCssClass = 'hd-icon-winclassic color-one';
        break;
      case 'WindowsPhone':
        platformCssClass = 'hd-icon-winmodern';
        break;
      case 'WindowsRuntime':
      case 'WindowsDesktop':
        platformCssClass = 'hd-icon-winclassic color-two';
        break;
      case 'Apple':
        platformCssClass = 'hd-icon-apple';
        break;
      case 'Android':
        platformCssClass = 'hd-icon-android';
        break;
      case 'AndroidPlus':
        platformCssClass = 'hd-icon-androidplus';
        break;
      /* Scanner and Printers have same Icons (hd-icon-printer), however there is no specific icon for Scanners */
      case 'Scanner':
        platformCssClass = 'hd-icon-printer';
        break;
      /* Printers are of type ZebraPrinters for which we have hd-icon-zebra icon class*/
      case 'Printer':
        platformCssClass = 'hd-icon-zebra';
        break;
      case 'Linux':
        platformCssClass = 'hd-icon-type-Linux';
        break;
      case 'Unmanaged':
        platformCssClass = 'hd-icon-alldevice-alt';
        break;
      default:
        platformCssClass = 'hd-icon-unknown';
    }

    return platformCssClass;
  };

  private generatePlatformIconString = (icon: string): string => {
    const result = 'hd-icon icon-sm ' + icon;
    return result;
  };

  /**
   * Sets group id header.
   * @param headers HttpHeaders to append to
   * @return A new HttpHeaders object with the group id set
   */
  private addGroupHeaders(headers: HttpHeaders): HttpHeaders {
    if (this.featureToggleService.isEnabled('Groups')) {
      const groupId = this.groupContextService.currentGroupId;
      if (groupId && !headers.has(this.GROUP_HEADER_ID)) {
        headers.set(this.GROUP_HEADER_ID, groupId.toString());
      }
    }
    return headers;
  }

  public getDevices = (deviceIds?: string): Observable<IDeviceModel[]> => {
    const options = {
      params: { deviceIds: deviceIds },
    };

    return this.http.get<IDeviceModel[]>(this.appSettingsService.deviceServiceUrl, options);
  };

  /**
   * Returns the _single_ css class representing the icon for the given device
   * @param icon The device css class string
   * @param connectionName
   * @param size Size of the icon, if not specified , it's icon-xxxlg
   */
  public generateDeviceIconString = (icon: string, connectionName: string, size?: string): string => {
    let result = '';
    const iconSize = size || 'icon-xxxlg';
    if (icon !== '' && !!connectionName) {
      result = 'hd-icon middle overlap online ' + icon + ' ' + iconSize;
    } else {
      result = 'hd-icon middle overlap offline ' + icon + ' ' + iconSize;
    }
    return result;
  };

  /**
   * Returns the _single_ css class representing the icon for the given device
   * @param deviceFamily The device family (or platform)
   */
  public getDeviceIconClass = (deviceFamily: string, size?: string): string => {
    return this.generateDeviceIconString(this.getDeviceCssClass(deviceFamily, ''), size);
  };

  /**
   * Returns the _single_ css class representing the icon for the given device
   * @param deviceFamily The device family (or platform)
   * @param connectionName
   * @param size
   */
  public getDeviceScreenIconClass = (deviceFamily: string, connectionName: string, size?: string): string => {
    const deviceScreenIconClass = this.generateDeviceIconString(
      this.getDeviceCssClass(deviceFamily, 'screen'),
      connectionName,
      size
    );
    return deviceScreenIconClass;
  };

  /**
   * Returns the _single_ css class representing the icon for the given device
   * @param deviceFamily The device family (or platform)
   */
  getDeviceCssClass(deviceFamily: string, suffix: string): string {
    let deviceIconClass = '';

    switch (deviceFamily) {
      case 'Android':
      case 'AndroidPlus':
      case 'WindowsPhone':
      case 'Apple':
        deviceIconClass = 'hd-icon-phone' + suffix;
        break;
      case 'AppleDesktop':
      case 'Windows':
      case 'WindowsRuntime':
      case 'WindowsDesktop':
        deviceIconClass = 'hd-icon-desktop' + suffix;
        break;
      case 'WindowsCE':
        deviceIconClass = 'hd-icon-' + suffix + (suffix !== '' ? '-' : '') + 'DeviceWindowsCE';
        break;
      case 'Scanner':
      case 'Printer':
        deviceIconClass = 'hd-icon-printer' + suffix;
        break;
      case 'Linux':
      // Reusing Linux graphics temporarily.
      case 'Tizen':
        deviceIconClass = 'hd-icon-DeviceLinux' + suffix;
        break;
      case 'Unmanaged':
        deviceIconClass = 'hd-icon-alldevice-alt' + suffix;
        break;
      default:
        deviceIconClass = 'hd-icon-alldevice-alt unknown-device-icon' + suffix;
        break;
    }
    return deviceIconClass;
  }

  searchGDPRDevices(searchString: string): Observable<DeviceInfoModel[]> {
    return this.http.post<DeviceInfoModel[]>(
      '/sotixsight/deviceAnalysis/dashboard/gdpr/search',
      JSON.stringify(searchString)
    );
  }

  anonymizeDeviceInfo(devId: string): Observable<any> {
    return this.http.post<any>(`/sotixsight/deviceAnalysis/dashboard/gdpr/${devId}`, {});
  }

  searchDeviceModels(searchString: string, manufacturer: string): Observable<string[]> {
    const params = new HttpParams().set('searchString', searchString).set('manufacturer', manufacturer);
    return this.http.get<string[]>('/sotixsight/deviceAnalysis/dashboard/deviceModel/search', { params });
  }

  searchManufacturers(searchString: string): Observable<string[]> {
    const params = new HttpParams().set('searchString', searchString);
    return this.http.get<string[]>('/sotixsight/deviceAnalysis/dashboard/manufacturers/search', { params });
  }
}
